import { useEffect, useMemo, useRef, useState } from 'react';
import { getCountries, getCountryCallingCode, CountryCode } from 'libphonenumber-js';
import { ChevronDown, Search, Check } from 'lucide-react';
import clsx from 'clsx';

/** ISO 3166 alpha-2 → flag emoji via regional indicator symbols. */
const flagOf = (iso: string) =>
  iso.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

// Intl.DisplayNames gives real country names with no extra dependency, so the
// list reads "United Arab Emirates" instead of a bare "AE".
const regionNames = (() => {
  try { return new Intl.DisplayNames(['en'], { type: 'region' }); }
  catch { return null; }
})();
const nameOf = (iso: string) => regionNames?.of(iso) ?? iso;

/** Markets Slightor tenants use most — pinned above the full list. */
const PRIORITY: CountryCode[] = ['AE', 'SA', 'PK', 'QA', 'KW', 'BH', 'OM'];

interface Props {
  value: CountryCode;
  onChange: (c: CountryCode) => void;
  className?: string;
  disabled?: boolean;
}

/**
 * Searchable country + dial-code picker. Replaces a native <select> of ~250
 * bare ISO codes — unusable without search, and unreadable without names.
 * Filters on country name, ISO code and dial code, so "uae", "AE", "971" and
 * "+971" all find the same row.
 */
export const CountrySelect: React.FC<Props> = ({ value, onChange, className, disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const all = useMemo(() => {
    const rows = getCountries().map((iso) => ({
      iso,
      name: nameOf(iso),
      dial: getCountryCallingCode(iso),
    }));
    const priority = PRIORITY
      .map((p) => rows.find((r) => r.iso === p))
      .filter((r): r is NonNullable<typeof r> => !!r);
    const rest = rows
      .filter((r) => !PRIORITY.includes(r.iso as CountryCode))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...priority, ...rest];
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase().replace(/^\+/, '');
    if (!term) return all;
    return all.filter((r) =>
      r.name.toLowerCase().includes(term)
      || r.iso.toLowerCase().includes(term)
      || r.dial.startsWith(term));
  }, [all, q]);

  useEffect(() => { setHighlight(0); }, [q]);

  useEffect(() => {
    if (!open) return;
    // Focus search on open so typing filters immediately.
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', onDown); };
  }, [open]);

  const pick = (iso: string) => {
    onChange(iso as CountryCode);
    setOpen(false);
    setQ('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => {
        const next = e.key === 'ArrowDown'
          ? Math.min(h + 1, filtered.length - 1)
          : Math.max(h - 1, 0);
        listRef.current?.querySelectorAll('button')[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    }
    if (e.key === 'Enter' && filtered[highlight]) { e.preventDefault(); pick(filtered[highlight].iso); }
  };

  const current = all.find((r) => r.iso === value);

  return (
    <div className={clsx('relative', className)} ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-text-primary transition-colors hover:bg-surface-alt focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span aria-hidden>{flagOf(value)}</span>
        <span className="tabular-nums">+{current?.dial ?? ''}</span>
        <ChevronDown size={13} className="text-text-muted" />
      </button>

      {open && (
        <div
          className="absolute left-0 z-40 mt-1 w-72 overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-elevated"
          onKeyDown={onKeyDown}
        >
          <div className="relative border-b border-border p-2">
            <Search size={13} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country or code…"
              className="w-full rounded-lg border border-border bg-surface py-1.5 pl-7 pr-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div ref={listRef} className="max-h-64 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-text-muted">No country matches “{q}”.</p>
            ) : filtered.map((r, i) => (
              <button
                key={r.iso}
                type="button"
                role="option"
                aria-selected={r.iso === value}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(r.iso)}
                className={clsx(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                  i === highlight ? 'bg-surface-alt' : 'hover:bg-surface-alt',
                )}
              >
                <span aria-hidden className="text-base leading-none">{flagOf(r.iso)}</span>
                <span className="min-w-0 flex-1 truncate text-text-primary">{r.name}</span>
                <span className="shrink-0 tabular-nums text-xs text-text-muted">+{r.dial}</span>
                {r.iso === value && <Check size={13} className="shrink-0 text-primary" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
